import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { addComment, subscribeComments } from '../services/db';

const formatTime = (ts) => {
  if (!ts?.seconds) return 'Gerade eben';
  return new Date(ts.seconds * 1000).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export default function ClientPreview({ project, onChange }) {
  const [comments, setComments]   = useState([]);
  const [linkInput, setLinkInput] = useState(project.driveLink || '');
  const [linkSaved, setLinkSaved] = useState(!!project.driveLink);
  const [form, setForm]           = useState({ text: '', timestamp: '', type: 'general' });
  const [sending, setSending]     = useState(false);
  const [copied, setCopied]       = useState(false);
  const commentsEndRef            = useRef(null);

  useEffect(() => {
    const unsub = subscribeComments(project.shareToken, setComments);
    return () => unsub();
  }, [project.shareToken]);

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  const shareUrl = `${window.location.origin}/s/${project.shareToken}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const saveLink = () => {
    onChange({ ...project, driveLink: linkInput }, true);
    setLinkSaved(true);
  };

  const handleSend = async () => {
    if (!form.text.trim()) return;
    setSending(true);
    try {
      await addComment(project.shareToken, {
        text: form.text.trim(),
        name: 'Ich (intern)',
        author: 'Ich',
        timestamp: form.type === 'timestamp' ? form.timestamp : null,
        type: form.type,
        source: 'owner',
      });
      setForm(f => ({ ...f, text: '', timestamp: '' }));
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ height: 'calc(100vh - 56px)' }} className="flex flex-col overflow-hidden">
      {/* Banner */}
      <div className="bg-indigo-600 px-6 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white text-xs">
          <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
            <path d="M8 3C4 3 1 8 1 8s3 5 7 5 7-5 7-5-3-5-7-5z" stroke="white" strokeWidth="1.4"/>
            <circle cx="8" cy="8" r="2" stroke="white" strokeWidth="1.4"/>
          </svg>
          <span className="font-medium">Kunden-Preview</span>
          <span className="opacity-60">– So sieht dein Kunde den Link</span>
        </div>
        <div className="flex items-center gap-2">
          <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-lg transition-all flex items-center gap-1.5">
            <svg width="10" height="10" viewBox="0 0 14 14" fill="none"><path d="M6 2H2a1 1 0 00-1 1v9a1 1 0 001 1h9a1 1 0 001-1V8M8 1h5v5M13 1L7 7" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Link öffnen
          </a>
          <button onClick={copyLink} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-lg transition-all">
            {copied ? 'Kopiert' : 'Link kopieren'}
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* LEFT */}
        <div className="flex-1 overflow-y-auto bg-white border-r border-gray-100">
          <div className="max-w-2xl mx-auto px-8 py-10">
            <div className="mb-8">
              <h1 className="text-2xl font-semibold text-gray-900">{project.title}</h1>
              <p className="text-sm text-gray-400 mt-1">{project.createdAt}</p>
            </div>

            <div className="mb-8">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest mb-3">Fertiges Video</p>
              {linkSaved && project.driveLink ? (
                <div className="rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="aspect-video bg-gray-900 flex flex-col items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-3">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 3L19 12L5 21V3Z" fill="white"/></svg>
                    </div>
                    <a href={project.driveLink} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-300 hover:text-indigo-200 underline underline-offset-2 mt-2">Im Drive öffnen →</a>
                  </div>
                  <div className="p-3 bg-gray-50 flex items-center justify-between">
                    <p className="text-xs text-gray-400 truncate">{project.driveLink}</p>
                    <button onClick={() => { setLinkSaved(false); setLinkInput(''); onChange({ ...project, driveLink: '' }, true); }} className="text-xs text-gray-400 hover:text-gray-600 ml-3 shrink-0">Ändern</button>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border-2 border-dashed border-gray-200 p-5">
                  <p className="text-sm text-gray-400 mb-3">Google Drive Link einfügen:</p>
                  <div className="flex gap-2">
                    <input value={linkInput} onChange={e => setLinkInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveLink()} placeholder="https://drive.google.com/…" className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50" />
                    <button onClick={saveLink} disabled={!linkInput.trim()} className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-all">Speichern</button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest mb-5">Skript & Konzept</p>
              <div className="flex flex-col gap-6">
                {project.blocks?.map(block => (
                  <div key={block.id}>
                    {block.type === 'text' && block.content && (
                      <div className="prose-custom"><ReactMarkdown>{block.content}</ReactMarkdown></div>
                    )}
                    {block.type === 'image' && block.imageUrl && (
                      <div className="rounded-2xl overflow-hidden border border-gray-100">
                        <img src={block.imageUrl} alt={block.imageCaption || ''} className="w-full object-cover" />
                        {block.imageCaption && <p className="text-xs text-gray-400 px-4 py-2 bg-gray-50">{block.imageCaption}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Feedback */}
        <div className="w-80 flex flex-col bg-gray-50 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-white">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-800">Feedback</p>
              <div className="flex items-center gap-2">
                {comments.length > 0 && <span className="text-xs bg-indigo-100 text-indigo-600 font-medium px-2 py-0.5 rounded-full">{comments.length}</span>}
                <span className="flex items-center gap-1 text-xs text-emerald-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">Kunden-Kommentare erscheinen in Rot</p>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col gap-3">
            {comments.length === 0 && (
              <div className="text-center py-12">
                <p className="text-sm text-gray-300">Noch kein Feedback</p>
              </div>
            )}
            {comments.map(c => {
              const isClient = c.source === 'client';
              return (
                <div key={c.id} className={`rounded-xl p-4 border shadow-sm ${isClient ? 'border-red-100 bg-red-50/60' : 'border-gray-100 bg-white'}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${isClient ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-600'}`}>
                        {(c.name || c.author || 'K')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className={`text-xs font-medium ${isClient ? 'text-red-700' : 'text-gray-700'}`}>
                          {c.name || c.author || 'Kunde'}
                          {isClient && <span className="ml-1 text-xs bg-red-100 text-red-500 px-1 rounded font-normal">Kunde</span>}
                        </p>
                        <p className="text-xs text-gray-400">{formatTime(c.createdAt)}</p>
                      </div>
                    </div>
                    {c.timestamp && <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${isClient ? 'bg-red-100 text-red-600' : 'bg-indigo-50 text-indigo-500'}`}>{c.timestamp}</span>}
                  </div>
                  <p className={`text-sm leading-relaxed ${isClient ? 'text-red-800' : 'text-gray-600'}`}>{c.text}</p>
                </div>
              );
            })}
            <div ref={commentsEndRef} />
          </div>

          <div className="border-t border-gray-100 bg-white px-4 py-4">
            <div className="flex gap-1.5 mb-3">
              <button onClick={() => setForm(f => ({ ...f, type: 'general' }))} className={`flex-1 text-xs py-1.5 rounded-lg font-medium transition-all ${form.type === 'general' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>Allgemein</button>
              <button onClick={() => setForm(f => ({ ...f, type: 'timestamp' }))} className={`flex-1 text-xs py-1.5 rounded-lg font-medium transition-all ${form.type === 'timestamp' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>Zeitstempel</button>
            </div>
            {form.type === 'timestamp' && (
              <input value={form.timestamp} onChange={e => setForm(f => ({ ...f, timestamp: e.target.value }))} placeholder="z. B. 00:42" className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-300 mb-2 font-mono" />
            )}
            <textarea value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(); }} placeholder="Eigene Anmerkung…" rows={3} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 transition-all resize-none leading-relaxed" />
            <button onClick={handleSend} disabled={!form.text.trim() || sending} className="w-full mt-2 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
              {sending && <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
              Senden
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
